import React, { useState, useEffect, useRef } from 'react';
import flame1 from '../assets/flame1.jpeg';
import flame2 from '../assets/flame2.jpeg';
import flame3 from '../assets/custom5.jpeg';
import flame4 from '../assets/flame-image-4.jpeg';

const FlameComponent = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const carouselRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  
  // Sample images for the carousel
  const images = [
    flame1,
    flame2,
    flame3,
    flame4,
  ];

  // Auto-rotate carousel
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isDragging.current) {
        setCurrentSlide((prev) => {
          const nextSlide = (prev + 1) % images.length;
          if (carouselRef.current) {
            carouselRef.current.scrollTo({
              left: nextSlide * carouselRef.current.offsetWidth,
              behavior: 'smooth'
            });
          }
          return nextSlide;
        });
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [images.length]);

  const handleDotClick = (index) => {
    setCurrentSlide(index);
    if (carouselRef.current) {
      carouselRef.current.scrollTo({
        left: index * carouselRef.current.offsetWidth,
        behavior: 'smooth'
      });
    }
  };

  // Update current slide based on scroll position
  const handleScroll = () => {
    if (carouselRef.current && !isDragging.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const width = carouselRef.current.offsetWidth;
      const newSlide = Math.round(scrollLeft / width);
      setCurrentSlide(newSlide);
    }
  };

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX - carouselRef.current.offsetLeft;
    scrollLeft.current = carouselRef.current.scrollLeft;
    carouselRef.current.style.cursor = 'grabbing';
    carouselRef.current.style.scrollBehavior = 'auto';
  };

  const handleMouseLeave = () => {
    if (isDragging.current) {
      isDragging.current = false;
      carouselRef.current.style.cursor = 'grab';
      carouselRef.current.style.scrollBehavior = 'smooth';
      snapToNearestSlide();
    }
  };

  const handleMouseUp = () => {
    isDragging.current = false;
    carouselRef.current.style.cursor = 'grab';
    carouselRef.current.style.scrollBehavior = 'smooth';
    snapToNearestSlide();
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - carouselRef.current.offsetLeft;
    const walk = (x - startX.current) * 2;
    carouselRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const snapToNearestSlide = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const width = carouselRef.current.offsetWidth;
      const newSlide = Math.round(scrollLeft / width);
      setCurrentSlide(newSlide);
      carouselRef.current.scrollTo({
        left: newSlide * width,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="flame-container">
      <div className="content-wrapper">
        <div className="text-content">
          <h1 className="main-title">A Flame That Cares</h1>
          
          <div className="section">
            <h2 className="section-title">Why We Believe In It</h2>
            <p className="section-text">
              At Realflame, we believe that access to clean and 
              safe cooking is a basic right, not a luxury. Our 
              motto, <span className="highlight">"A Flame That Cares"</span>, reflects our 
              commitment to designing appliances that care for 
              your health, your time, your environment, and your 
              wallet.
            </p>
          </div>

          <div className="section">
            <h2 className="section-title">What We Do About It</h2>
            <p className="section-text">
              We innovate relentlessly to produce gas stoves that 
              are not only high-performing but also fuel-efficient 
              and durable. We care deeply about our end users, 
              from urban homes in India to remote communities 
              in Africa and every Realflame product is a step 
              toward better cooking, healthier living, and a cleaner 
              planet.
            </p>
          </div>

          <button className="load-more-btn">
            Read More →
          </button>
        </div>

        <div className="carousel-container">
          <div 
            ref={carouselRef}
            className="carousel-wrapper"
            onScroll={handleScroll}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            {images.map((image, index) => (
              <div key={index} className="carousel-slide">
                <img 
                  src={image} 
                  alt={`Slide ${index + 1}`}
                  className="carousel-image"
                  draggable="false"
                />
              </div>
            ))}
          </div>
          
          <div className="carousel-dots">
            {images.map((_, index) => (
              <button
                key={index}
                className={`dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => handleDotClick(index)}
              />
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .flame-container {
          font-family: var(--font-family-primary);
          max-width: var(--max-width);
          margin: 0 auto;
          padding: var(--spacing-6) var(--left-right-padding);
          background: var(--color-white);
        }

        .content-wrapper {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--spacing-6);
          align-items: start;
        }
        
        section.who-are-we-section {
          padding-bottom: 0px;
        }
        
        .text-content {
          display: flex;
          flex-direction: column;
        }

        .main-title {
          font-size: var(--font-size-4);
          font-weight: 500;
          color: var(--color-black);
          margin-bottom: var(--spacing-3);
          line-height: 1.2;
        }

        .section {
          margin-bottom: var(--spacing-2);
        }

        .section-title {
          font-size: var(--font-size-3);
          font-weight: var(--font-weight-2);
          color: var(--color-1);
          margin-bottom: var(--spacing-2);
          font-style: italic;
        }

        .section-text {
          font-size: var(--font-size-2);
          font-weight: var(--font-weight-1);
          color: var(--color-black);
          line-height: 1.6;
          font-family: var(--font-family-secondary);
        }

        .highlight {
          font-weight: var(--font-weight-2);
          color: var(--color-1);
        }

        .load-more-btn {
          display: none;
          background: var(--color-1);
          color: var(--color-white);
          border: none;
          padding: var(--spacing-1) var(--spacing-1);
          font-size: var(--font-size-2);
          font-weight: var(--font-weight-2);
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.3s ease;
          align-self: flex-start;
        }

        .load-more-btn:hover {
          background: #e67419;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(245, 130, 32, 0.3);
        }

        .carousel-container {
          position: relative;
          border-radius: 0px;
          overflow: visible;
          display: flex;
          flex-direction: column;
          gap: var(--spacing-2);
        }

        .carousel-wrapper {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          overflow-x: scroll;
          overflow-y: hidden;
          scroll-snap-type: x mandatory;
          scroll-behavior: smooth;
          scrollbar-width: none;
          -ms-overflow-style: none;
          cursor: grab;
          user-select: none;
        }

        .carousel-wrapper::-webkit-scrollbar {
          display: none;
        }

        .carousel-slide {
          min-width: 100%;
          height: 100%;
          scroll-snap-align: start;
          position: relative;
        }

        .carousel-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          user-select: none;
          pointer-events: none;
        }

        .carousel-dots {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 8px;
          z-index: 10;
        }

        .dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          border: none;
          background: rgba(255, 255, 255, 0.5);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .dot.active {
          background: var(--color-1);
          transform: scale(1.2);
        }

        .dot:hover {
          background: rgba(255, 255, 255, 0.8);
        }

        .dot.active:hover {
          background: var(--color-1);
        }

        @media (max-width: 768px) {
          .content-wrapper {
            grid-template-columns: 1fr;
            gap: var(--spacing-4);
          }
          
          .carousel-container {
            height: 300px;
          }
          
          .main-title {
            font-size: var(--font-size-4);
          }
          
          .section-title {
            font-size: var(--font-size-3);
          }
          
          .section-text {
            font-size: var(--font-size-2);
          }
        }
      `}</style>
    </div>
  );
};

export default FlameComponent;